import { Avatar, AvatarFallback } from "@bowfast/ui/components/avatar";
import { Card, CardContent } from "@bowfast/ui/components/card";
import { Separator } from "@bowfast/ui/components/separator";
import { cn } from "@bowfast/ui/lib/utils";
import { motion } from "framer-motion";
import { memo } from "react";
import { fadeUp } from "~/lib/animations";
import { gradientForId } from "~/lib/gradients";
import { initials } from "~/lib/roles";
import type { CrewListMember } from "~/pages/crew/crew.types";

interface AtSeaSpotlightProps {
  members: CrewListMember[];
}

export const AtSeaSpotlight = memo(function AtSeaSpotlight({ members }: AtSeaSpotlightProps) {
  const atSea = members.filter((m) => m.user.crewMemberships.some((cm) => cm.expedition.status === "ACTIVE"));
  const expeditions = [
    ...new Set(
      atSea.flatMap((m) =>
        m.user.crewMemberships.filter((cm) => cm.expedition.status === "ACTIVE").map((cm) => cm.expedition.name),
      ),
    ),
  ];
  const combinedMiles = atSea.reduce((s, m) => s + m.user.totalMilesLogged, 0);

  return (
    <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.06 }}>
      <Card className="relative overflow-hidden border-0 bg-abyss-900 text-white">
        {/* Subtle gradient overlay */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 80% 40%, rgba(4,124,199,0.2) 0%, transparent 50%), radial-gradient(ellipse at 20% 90%, rgba(20,184,166,0.1) 0%, transparent 40%)",
          }}
          aria-hidden
        />
        <CardContent className="relative z-10 flex flex-wrap items-center gap-5 py-4">
          {/* Crew avatars at sea — stacked */}
          <div className="flex items-center">
            {atSea.map((m, i) => (
              <div
                key={m.id}
                className={cn("rounded-full bg-gradient-to-br p-[2px]", gradientForId(m.user.id), i > 0 && "-ml-3")}
              >
                <Avatar size="sm" className="size-8 border-2 border-abyss-900">
                  <AvatarFallback className="bg-abyss-800 text-[0.625rem] font-bold text-white">
                    {initials(m.user.firstName, m.user.lastName)}
                  </AvatarFallback>
                </Avatar>
              </div>
            ))}
          </div>

          <Separator orientation="vertical" className="!h-8 bg-white/10" />

          {/* Status text */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="relative flex size-2">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-shallow-400 opacity-60" />
                <span className="relative inline-flex size-2 rounded-full bg-shallow-400" />
              </span>
              <p className="text-sm font-semibold">{atSea.length} crew at sea</p>
            </div>
            <p className="mt-0.5 text-xs text-white/50">{expeditions.join(" · ")}</p>
          </div>

          {/* Mini stats */}
          <div className="flex items-center gap-4 text-xs">
            <div className="text-center">
              <p className="font-heading text-lg font-bold tabular-nums">
                {Math.round(combinedMiles).toLocaleString()}
              </p>
              <p className="text-white/40">nm combined</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
});
